import { Clock } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { CollapsibleSection } from "@/components/collapsible-section";
import { SimpleNoteTable } from "@/components/note-lists/simple-note-table";
import { useUpdateNoteMutation } from "@/mutations/notes";
import { useDeleteNoteWithUndo } from "@/hooks/useDeleteNoteWithUndo";
import type { Note } from "@/types/note";

interface DueTodaySectionProps {
  notes: Note[];
  className?: string;
}

export function DueTodaySection({ notes, className }: DueTodaySectionProps) {
  const updateNoteMutation = useUpdateNoteMutation();
  const { deleteNote } = useDeleteNoteWithUndo();

  if (notes.length === 0) return null;

  const togglingNotes = new Set<string>();
  if (updateNoteMutation.isPending && updateNoteMutation.variables?.noteId) {
    togglingNotes.add(updateNoteMutation.variables.noteId);
  }

  const handleToggleComplete = (noteId: string, completed: boolean) => {
    updateNoteMutation.mutate({
      noteId,
      status: completed ? "COMPLETED" : "NOT_STARTED",
    });
  };

  const handleDelete = (noteId: string) => {
    const note = notes.find((n) => n.id === noteId);
    if (note) deleteNote(note);
  };

  return (
    <CollapsibleSection
      title="Due Today"
      icon={<Clock className="h-4 md:h-5 w-4 md:w-5 text-amber-500" />}
      count={notes.length}
      className={className}
    >
      <SimpleNoteTable
        notes={notes}
        onToggleComplete={handleToggleComplete}
        onDelete={handleDelete}
        isTogglingComplete={togglingNotes}
      />
      {notes.length > 5 && (
        <Button variant="ghost" size="sm" asChild className="w-full mt-3">
          <Link to="/today">View all →</Link>
        </Button>
      )}
    </CollapsibleSection>
  );
}
